// Live API layer for the Procurement > Planning module.
//
// Implements api_contract/procurement/api-contract-planning.md — the Planning
// controller (route base `v1/Planning`) plus three supporting lookups on
// `ProcurementController` (`v1/Procurement`). Everything here lives on the
// CRUDE host (`06.WebApi.Crude` → crudeApi), verified via curl 2026-07-21
// (`v1/Planning` → 401 on crude, 404 on prohydro). The ONLY exception is the
// Cargo Category lookup, which the contract flags as "host WebApi terpisah" and
// curl confirms is on the ProHydro host (`v1/CargoCategories/Lookup` → 401 on
// prohydro, 404 on crude — consistent with AD-004: Crude Category = ProHydro).
//
// crude_api_helper unwraps `.data` on success and rejects errors as a plain
// string message, so callers do `toast.error(\`...: ${error}\`)`. Blob
// downloads (Excel / file preview) bypass the query-string client and use
// crudeAxios directly with responseType 'blob' (mirrors registrationApi's
// downloadCrudeDocument / AuditView export path).
import {
  crudeApi,
  crudeAxios,
  proHydroApi,
} from '../../../../helpers/crude_api_helper';
import {
  STEP_STATUS,
  STEP_STATUS_API,
  DOC_SOURCE,
  DOC_SOURCE_API,
} from './constants';
import { JAKARTA_UTC_OFFSET_HOURS } from '../formatters';

const PLANNING = '/v1/Planning';
const PROCUREMENT = '/v1/Procurement';

// ---------------------------------------------------------------------------
// Enum mapping (contract "Enum yang Dipakai" table)
// ---------------------------------------------------------------------------

// PlanningStepStatus: InProgress | Completed. The UI keeps human labels
// ('In Progress' / 'Completed'); the wire uses the C# member names.
export const stepStatusToApi = uiStatus =>
  uiStatus === STEP_STATUS.COMPLETED
    ? STEP_STATUS_API.COMPLETED
    : STEP_STATUS_API.IN_PROGRESS;

export const stepStatusFromApi = apiStatus =>
  apiStatus === STEP_STATUS_API.COMPLETED
    ? STEP_STATUS.COMPLETED
    : STEP_STATUS.IN_PROGRESS;

// ---------------------------------------------------------------------------
// Normalizers: API response shape → the field names the existing tabs render.
// Server ids needed for later mutations are preserved alongside the display
// fields (cargo.id / crude.id / clause.id / document.id are the mutation keys).
// ---------------------------------------------------------------------------

const normalizeCrude = cr => ({
  id: cr.id, // PlanningCargoCrude.Id — DELETE key (section 11)
  registrationId: cr.implementationCrudeId, // dedupe + React key
  implementationCrudeId: cr.implementationCrudeId,
  crudeName: cr.crudeName,
  maxCapacity: cr.maxCapacity ?? null,
  remarkMaxCapacity: cr.maxCapacityNote ?? '',
  isViaCibOnly: !!cr.isViaCIBOnly,
  bleeding1: cr.blendingCrude1 ?? null,
  bleeding2: cr.blendingCrude2 ?? null,
});

const normalizeCargo = c => ({
  id: c.id, // PlanningCargo.Id — DELETE / Crude routes (section 8/10/11)
  cargoType: c.cargoCategoryName,
  cargoCategoryId: c.cargoCategoryId,
  ot: c.otPercentage ?? null,
  otOption: c.operationalToleranceOption || null,
  volume: c.volumeKbbl ?? null,
  ddrFrom: c.ddrFrom || null,
  ddrTo: c.ddrTo || null,
  ddrNotes: c.ddrNotes || '',
  incoterms: (c.incoterms || []).map(i => i.incotermName),
  incotermIds: (c.incoterms || []).map(i => i.incotermId),
  incotermsNotes: c.incotermsNotes || '',
  plant: c.plantName,
  unit: c.cduName,
  cduId: c.cduId,
  crudes: (c.crudes || []).map(normalizeCrude),
});

const normalizeClause = c => ({
  id: c.id, // PlanningClause.Id — SaveMainRequirement key (section 6)
  clauseId: c.id,
  // Kategori master asal clause — dasar dropdown "-Add Template-" di editor.
  // null = clause ad-hoc (dropdown disembunyikan).
  templateCategoryId: c.templateInformationCategoryId || null,
  title: c.title,
  mandatory: !!c.isMandatoryForDmut,
  summernote: c.note || '',
});

const normalizeDocument = d => ({
  id: d.id, // PlanningDocument.Id — Delete/ShowToDmut/Download key
  name: d.documentName,
  source:
    d.source === DOC_SOURCE_API.MASTER ? DOC_SOURCE.MASTER : DOC_SOURCE.CUSTOM,
  sourceApi: d.source,
  showToDmut: !!d.showToDmut,
  fileName: d.fileName,
  fileContentType: d.fileContentType,
  fileSize: d.fileSize,
  storageFileId: d.storageFileId,
  // documents[].created/.modified added to GetPlanningResponse 2026-07-21
  // (contract line 90) for the Supporting Document tab's "Last Updated" column.
  lastUpdated: d.modified || d.created || null,
});

const normalizeDmutRow = x => ({
  id: x.id,
  dmutCode: x.dmutCode,
  dmutName: x.dmutName,
  dmutCargoCategory: x.dmutCargoCategory,
  dmutCountry: x.dmutCountry,
  pics: (x.pics || []).map(p => ({
    id: p.id,
    picName: p.picName,
    picEmail: p.picEmail,
  })),
});

// Full Detail (GetPlanningResponse) → { record, detail } for PlanningForm.
export const normalizePlanningDetail = api => {
  const record = {
    id: api.id, // Planning.Id — the {planningId} used in every route
    procurementId: api.procurementId,
    procId: api.procurementCode,
    title: api.title,
    procurementType: api.jenisPengadaanName,
    procurementMethod: api.mekanismePengadaanName,
    description: api.description,
    // ⚠️ `phaseStatusName` is ALWAYS the Procurement header's status, never
    // Planning's own (contract section 2, clarified 2026-07-23). Once the header
    // has moved on it reads that later stage's status — e.g. 'On Hard Recall'
    // belonging to Tender Opening — even though Planning itself is back in Draft.
    status: api.phaseStatusName,
    // Fase header (Planning/Procurement/Tender Opening). Fase sudah melewati
    // 'Planning' = Planning ini SUDAH submitted, apa pun bunyi phaseStatusName.
    mainStatus: api.phaseName,
    // Hard Recall (contract 2026-07-23): Tender Opening can push Planning back to
    // Draft while the Procurement header stays on a later phase. When true the
    // 13 mutation endpoints reopen, with two exceptions: Contract (contractType /
    // start / end) is frozen, and DeletePlanning stays locked. The contract
    // requires the FE to gate on this field rather than waiting for the 422.
    isHardRecall: !!api.isHardRecall,
    // Why / who / when the recall was raised — Detail only, added 2026-07-23.
    // Read from the newest ProcurementLogDetail with Action = "Hard Recall", not
    // from columns on Planning. All three stay populated after `isHardRecall`
    // flips back to false: they are the last recall's history, not live state.
    hardRecallReason: api.hardRecallReason || null,
    hardRecallBy: api.hardRecallBy || null,
    hardRecallAt: api.hardRecallAt || null,
  };

  const detail = {
    requirement: {
      contractType: api.contractType || '',
      startPeriod: api.contractStartPeriod || null,
      endPeriod: api.contractEndPeriod || null,
      cargos: (api.cargos || []).map(normalizeCargo),
      cargoNotes: api.cargoNotes || '',
      clauses: (api.clauses || []).map(normalizeClause),
      additionalNotes: api.otherTermsNotes || '',
    },
    supportingDocuments: (api.documents || []).map(normalizeDocument),
    dmut: {
      // A saved-and-reloaded planning already has its invitees persisted; treat
      // them as the confirmed, invited set. Generate (section 19) re-populates
      // the pick list transiently on top of this.
      confirmed: (api.dmutInvitations || []).length > 0,
      generated: false,
      rows: (api.dmutInvitations || []).map(x => ({
        ...normalizeDmutRow(x),
        invited: true,
      })),
    },
    steps: {
      requirement: stepStatusFromApi(api.mainRequirementStepStatus),
      supportingDocument: stepStatusFromApi(api.supportingDocumentStepStatus),
      dmut: stepStatusFromApi(api.dmutInvitationStepStatus),
    },
  };

  return { record, detail };
};

// List item (GetPlanningListResponse) → the fields PlanningView's columns read.
// `id` is Planning.Id (used as {planningId} for navigation), NOT Procurement.Id.
export const normalizeListItem = r => ({
  id: r.id,
  procurementId: r.procurementId,
  procId: r.procurementCode,
  title: r.title,
  procurementType: r.jenisPengadaanName,
  procurementMethod: r.mekanismePengadaanName,
  description: r.description,
  // Contract period (Term/Spot) — kolom "Period" di list; null selama belum diisi.
  contractType: r.contractType || null,
  // ⚠️ Both of these are the Procurement HEADER's phase/status, not Planning's
  // own (contract section 2, clarified 2026-07-23) — so `mainStatus` is whatever
  // stage the tender has reached ('Planning' | 'Procurement' | 'Tender Opening'
  // | ...) and `status` that stage's status, which is why a Planning that is
  // perfectly editable can show up here as 'On Hard Recall'. Use `isHardRecall`
  // below — never this text — to decide what the row may do.
  mainStatus: r.phaseName,
  status: r.phaseStatusName,
  createdDate: r.created,
  updatedDate: r.modified,
  mainRequirementStepStatus: r.mainRequirementStepStatus,
  supportingDocumentStepStatus: r.supportingDocumentStepStatus,
  dmutInvitationStepStatus: r.dmutInvitationStepStatus,
  // Hard Recall (contract 2026-07-23) — a row can read status 'Draft' while
  // being mid-Hard-Recall, in which case Delete is still refused (422). The list
  // needs the field to keep the Delete action off those rows.
  isHardRecall: !!r.isHardRecall,
});

// ===========================================================================
// Section 1 / 2 / 3 / 4 / 5 — Planning CRUD + Excel
// ===========================================================================

// 1 — POST /v1/Planning. Returns { procurementId, procurementCode, planningId }.
export const createPlanning = ({
  jenisPengadaanId,
  title,
  mekanismePengadaanId,
  description,
}) =>
  crudeApi.create(PLANNING, {
    jenisPengadaanId,
    title,
    mekanismePengadaanId,
    description: description || undefined,
  });

// 2 — GET /v1/Planning (paginated). `sortField` is PascalCase + whitelisted
// (section 2); `sortOrder` only applies when a field is set. `isHardRecall`
// (added 2026-07-23) is a tri-state filter: true = only Planning currently
// recalled, false = only those that are not, null/undefined = no filter at all —
// so it must be passed through as `undefined`, not coerced to a boolean, or
// "All" would silently become "not recalled". Returns { items, totalCount }.
export const listPlanning = ({
  page = 1,
  pageSize = 10,
  searchText = '',
  sortField = null,
  sortOrder = null,
  isHardRecall = null,
} = {}) =>
  crudeApi
    .get(PLANNING, {
      page,
      pageSize,
      searchText: searchText || undefined,
      sortField: sortField || undefined,
      sortOrder: sortField ? sortOrder || undefined : undefined,
      isHardRecall:
        typeof isHardRecall === 'boolean' ? isHardRecall : undefined,
    })
    .then(res => ({
      items: (res?.items || []).map(normalizeListItem),
      totalCount: res?.totalCount ?? 0,
    }));

// 2a — GET /v1/Planning/GetPlanningSummary. No query params — always counts ALL
// non-deleted Planning (unaffected by list search/filter). Returns
// { totalPlanning, draft, submitted } (totalPlanning === draft + submitted).
export const getPlanningSummary = () =>
  crudeApi.get(`${PLANNING}/GetPlanningSummary`).then(res => ({
    totalPlanning: res?.totalPlanning ?? 0,
    draft: res?.draft ?? 0,
    submitted: res?.submitted ?? 0,
  }));

// 3 — GET /v1/Planning/GetPlanningListExcel (blob). Same filter params as List
// minus pagination; 404 when the filtered result is empty. `isHardRecall` is
// forwarded too so the export matches what the list is showing.
//
// ⚠️ The dates inside this file do NOT go through the automatic `+00:00`
// normalisation every JSON GET gets (section 3) — a static file has no client to
// convert it. Instead the server renders Created/Updated Date at the
// `utcOffsetHours` we send (added 2026-07-24).
//
// Pinned to Jakarta (+7), matching how ../formatters.js renders the same values
// on screen, so the exported file and the list agree cell-for-cell. The contract
// suggests sending the viewer's real offset instead, but that would only be
// right if the screens were also per-viewer — they are deliberately not (these
// are Pertamina-internal screens that must read the same on every machine).
// `localUtcOffsetHours()` is the ready-made swap if that ever changes; both ends
// have to move together.
export const downloadPlanningExcel = ({
  searchText = '',
  sortField = null,
  sortOrder = null,
  isHardRecall = null,
  utcOffsetHours = JAKARTA_UTC_OFFSET_HOURS,
} = {}) => {
  const params = new URLSearchParams();
  if (searchText) params.append('searchText', searchText);
  if (sortField) {
    params.append('sortField', sortField);
    if (sortOrder) params.append('sortOrder', sortOrder);
  }
  if (typeof isHardRecall === 'boolean') {
    params.append('isHardRecall', String(isHardRecall));
  }
  params.append(
    'utcOffsetHours',
    String(
      typeof utcOffsetHours === 'number'
        ? utcOffsetHours
        : JAKARTA_UTC_OFFSET_HOURS
    )
  );
  const qs = params.toString();
  return crudeAxios.get(
    `${PLANNING}/GetPlanningListExcel${qs ? `?${qs}` : ''}`,
    { responseType: 'blob' }
  );
};

// 4 — GET /v1/Planning/{planningId}. Returns normalized { record, detail }.
export const getPlanning = planningId =>
  crudeApi.get(`${PLANNING}/${planningId}`).then(normalizePlanningDetail);

// 5 — DELETE /v1/Planning/{planningId} (204). 422 if already Submitted.
export const deletePlanning = planningId =>
  crudeApi.delete(`${PLANNING}/${planningId}`);

// ===========================================================================
// Section 6 — Main Requirement (contract + clauses + other terms)
// ===========================================================================

// PUT /v1/Planning/{planningId}/SaveMainRequirement. `stepStatus` is a UI label
// mapped to the API enum here. `clauses` come straight from the normalized
// detail (id/mandatory/summernote).
//
// During a Hard Recall the server IGNORES contractType/contractStartPeriod/
// contractEndPeriod outright — whatever is sent, the stored Contract is left
// alone and no error is raised (section 6, changed 2026-07-23; the previous
// version compared them for equality and 422'd on any difference, which misfired
// on serialisation-level differences). So nothing special is needed here: send
// the tab's current values as-is. The UI still disables the Contract inputs
// during a recall, which the contract calls good practice but no longer
// requires. Cargo / Clause / Other Terms in the same request are processed
// normally either way.
export const saveMainRequirement = (
  planningId,
  {
    stepStatus,
    contractType,
    startPeriod,
    endPeriod,
    clauses,
    additionalNotes,
    cargoNotes,
  }
) =>
  crudeApi.put(`${PLANNING}/${planningId}/SaveMainRequirement`, {
    stepStatus: stepStatusToApi(stepStatus),
    contractType: contractType || null,
    contractStartPeriod: startPeriod || null,
    contractEndPeriod: endPeriod || null,
    clauses: (clauses || []).map(c => ({
      id: c.id,
      isMandatoryForDmut: !!c.mandatory,
      note: c.summernote || '',
    })),
    otherTermsNotes: additionalNotes || undefined,
    cargoNotes: cargoNotes || undefined,
  });

// ===========================================================================
// Section 7 / 8 — Cargo add / delete
// ===========================================================================

// POST /v1/Planning/{planningId}/Cargo. Returns { cargoId }.
export const addCargo = (planningId, cargo) =>
  crudeApi.create(`${PLANNING}/${planningId}/Cargo`, {
    cargoCategoryId: cargo.cargoCategoryId,
    otPercentage: cargo.otPercentage,
    operationalToleranceOption: cargo.operationalToleranceOption || undefined,
    volumeKbbl: cargo.volumeKbbl ?? undefined,
    ddrFrom: cargo.ddrFrom || undefined,
    ddrTo: cargo.ddrTo || undefined,
    ddrNotes: cargo.ddrNotes || undefined,
    cduId: cargo.cduId,
    incotermsNotes: cargo.incotermsNotes || undefined,
    incotermIds: cargo.incotermIds || [],
  });

// DELETE /v1/Planning/{planningId}/Cargo/{cargoId} (204).
export const deleteCargo = (planningId, cargoId) =>
  crudeApi.delete(`${PLANNING}/${planningId}/Cargo/${cargoId}`);

// ===========================================================================
// Section 9 / 10 / 11 — Crude lookup / add / delete (per cargo)
// ===========================================================================

// GET /v1/Planning/{planningId}/Cargo/{cargoId}/LookupImplementationCrude.
// Returns the raw items [{ id (implementationCrudeId), text, technicalLimit }].
// The cargo must already exist server-side (has a cargoId) — the lookup filters
// by Cargo.CduId + Cargo.CargoCategoryName, so add the cargo first.
export const lookupImplementationCrude = (planningId, cargoId) =>
  crudeApi
    .get(`${PLANNING}/${planningId}/Cargo/${cargoId}/LookupImplementationCrude`)
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// POST /v1/Planning/{planningId}/Cargo/{cargoId}/Crude. Returns { cargoCrudeId }.
// `maxCapacity` is validated by the server against ImplementationCrude
// .TechnicalLimit (the lookup's `technicalLimit`), NOT ImplementationLimit.
export const addCargoCrude = (planningId, cargoId, crude) =>
  crudeApi.create(`${PLANNING}/${planningId}/Cargo/${cargoId}/Crude`, {
    implementationCrudeId: crude.implementationCrudeId,
    maxCapacity: crude.maxCapacity,
    maxCapacityNote: crude.maxCapacityNote || undefined,
    isViaCIBOnly: !!crude.isViaCIBOnly,
    blendingCrude1: crude.blendingCrude1 ?? undefined,
    blendingCrude2: crude.blendingCrude2 ?? undefined,
  });

// DELETE /v1/Planning/{planningId}/Cargo/{cargoId}/Crude/{cargoCrudeId} (204).
export const deleteCargoCrude = (planningId, cargoId, cargoCrudeId) =>
  crudeApi.delete(
    `${PLANNING}/${planningId}/Cargo/${cargoId}/Crude/${cargoCrudeId}`
  );

// ===========================================================================
// Section 12–18 — Supporting Documents
// ===========================================================================

// 12 — GET /v1/Planning/{planningId}/Document/LookupFromMaster?searchText=.
// Top-5 CrudeDocumentUpload (DocumentType=Procurement) minus ones already added.
// ---------------------------------------------------------------------------
// Clause — lookup / add-from-template / add ad-hoc / delete. Mirrors the
// Document FromMaster flow: the lookup already excludes templates that are in
// this Planning, and eligibility (active category + matching Jenis Pengadaan)
// matches the CreatePlanning seeding rule.
// ---------------------------------------------------------------------------

// Rows = Clausa CATEGORY yang belum ada di Planning:
// { id, categoryName, templateCount }. Menambahkan kategori membuat clause
// KOSONG — isinya diambil user lewat dropdown "-Add Template-" di editor.
export const lookupClauseFromTemplate = planningId =>
  crudeApi
    .get(`${PLANNING}/${planningId}/Clause/LookupFromTemplate`)
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// POST .../Clause/FromTemplate — body { clauses: [{
// templateInformationCategoryId, isMandatoryForDmut }] }. Returns { clauseIds }.
export const addClausesFromTemplate = (planningId, clauses) =>
  crudeApi.create(`${PLANNING}/${planningId}/Clause/FromTemplate`, {
    clauses: (clauses || []).map(c => ({
      templateInformationCategoryId: c.templateInformationCategoryId,
      isMandatoryForDmut: !!c.isMandatoryForDmut,
    })),
  });

// Opsi dropdown "-Add Template-" sebuah clause: Clausa Information milik
// kategori clause tsb. Rows: { id, title, contents }.
export const getClauseTemplates = (planningId, clauseId) =>
  crudeApi
    .get(`${PLANNING}/${planningId}/Clause/${clauseId}/Templates`)
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// POST .../Clause — ad-hoc clause (not linked to master). Returns { clauseId }.
export const addClause = (planningId, { title, isMandatoryForDmut, note }) =>
  crudeApi.create(`${PLANNING}/${planningId}/Clause`, {
    title,
    isMandatoryForDmut: !!isMandatoryForDmut,
    note,
  });

export const deleteClause = (planningId, clauseId) =>
  crudeApi.delete(`${PLANNING}/${planningId}/Clause/${clauseId}`);

export const lookupDocumentFromMaster = (planningId, searchText = '') =>
  crudeApi
    .get(`${PLANNING}/${planningId}/Document/LookupFromMaster`, {
      searchText: searchText || undefined,
    })
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// 13 — POST /v1/Planning/{planningId}/Document/FromMaster. Returns { documentIds }.
export const addDocumentsFromMaster = (planningId, documents) =>
  crudeApi.create(`${PLANNING}/${planningId}/Document/FromMaster`, {
    documents: (documents || []).map(d => ({
      crudeDocumentUploadId: d.crudeDocumentUploadId,
      showToDmut: d.showToDmut !== false,
    })),
  });

// 14 — POST /v1/Planning/{planningId}/Document/Upload (multipart, THE only
// non-JSON write in this contract). Field casing matches the [FromForm] binder:
// DocumentName / ShowToDmut / File. Returns { documentId }.
export const uploadDocument = (
  planningId,
  { documentName, showToDmut, file }
) =>
  crudeApi.postMultipart(`${PLANNING}/${planningId}/Document/Upload`, {
    DocumentName: documentName,
    ShowToDmut: showToDmut ? 'true' : 'false',
    File: file,
  });

// 15 — DELETE /v1/Planning/{planningId}/Document/{documentId} (204).
export const deleteDocument = (planningId, documentId) =>
  crudeApi.delete(`${PLANNING}/${planningId}/Document/${documentId}`);

// 16 — PUT /v1/Planning/{planningId}/Document/{documentId}/ShowToDmut (204).
export const setDocumentShowToDmut = (planningId, documentId, showToDmut) =>
  crudeApi.put(`${PLANNING}/${planningId}/Document/${documentId}/ShowToDmut`, {
    showToDmut: !!showToDmut,
  });

// 17 — PUT /v1/Planning/{planningId}/Document/Status (204). Tab-level
// Save / Save & Completed for Supporting Document.
export const saveDocumentStatus = (planningId, stepStatus) =>
  crudeApi.put(`${PLANNING}/${planningId}/Document/Status`, {
    stepStatus: stepStatusToApi(stepStatus),
  });

// 18 — GET /v1/Planning/{planningId}/Document/{documentId}/Download (blob).
export const downloadDocument = (planningId, documentId) =>
  crudeAxios.get(`${PLANNING}/${planningId}/Document/${documentId}/Download`, {
    responseType: 'blob',
  });

// ===========================================================================
// Section 19–21 — DMUT Invitation
// ===========================================================================

// 19 — GET /v1/Planning/{planningId}/DmutInvitation/Generate. LIVE call to eRS
// (needs Pertamina internal network / VPN — 422 "Gagal mengambil data DMUT..."
// otherwise). Not persisted. Returns nested-per-company rows (pics[] inside).
export const generateDmutInvitation = planningId =>
  crudeApi
    .get(`${PLANNING}/${planningId}/DmutInvitation/Generate`)
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// 20 — GET /v1/Planning/{planningId}/DmutInvitation/Download (blob, also live eRS).
export const downloadDmutInvitation = planningId =>
  crudeAxios.get(`${PLANNING}/${planningId}/DmutInvitation/Download`, {
    responseType: 'blob',
  });

// 21 — PUT /v1/Planning/{planningId}/DmutInvitation (204). Replace-total: send
// ONLY the checked rows; the server wipes and re-inserts. `items` maps the
// nested-per-company shape (dmutCode/dmutName/dmutCargoCategory/dmutCountry +
// pics[{picName,picEmail}]).
export const saveDmutInvitation = (planningId, { stepStatus, items }) =>
  crudeApi.put(`${PLANNING}/${planningId}/DmutInvitation`, {
    stepStatus: stepStatusToApi(stepStatus),
    items: (items || []).map(x => ({
      dmutCode: x.dmutCode,
      dmutName: x.dmutName,
      dmutCargoCategory: x.dmutCargoCategory,
      dmutCountry: x.dmutCountry,
      pics: (x.pics || []).map(p => ({
        picName: p.picName || null,
        picEmail: p.picEmail || null,
      })),
    })),
  });

// ===========================================================================
// Section 22 — Submit (locks Planning, opens the Procurement stage)
// ===========================================================================

// POST /v1/Planning/{planningId}/Submit (no body, 204).
export const submitPlanning = planningId =>
  crudeApi.create(`${PLANNING}/${planningId}/Submit`, {});

// ===========================================================================
// Section 0 — Supporting lookups (ProcurementController + master hosts)
// ===========================================================================

// GET /v1/Procurement/LookupJenisPengadaan (crude). ListResponse<LookupDto>.
export const lookupJenisPengadaan = () =>
  crudeApi
    .get(`${PROCUREMENT}/LookupJenisPengadaan`)
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// GET /v1/Procurement/LookupMekanismePengadaan?jenisPengadaanId= (crude).
// jenisPengadaanId is REQUIRED (404 if omitted).
export const lookupMekanismePengadaan = jenisPengadaanId =>
  crudeApi
    .get(`${PROCUREMENT}/LookupMekanismePengadaan`, { jenisPengadaanId })
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// GET /v1/Procurement/LookupIncoterm (crude). Purpose-built so `id` is the real
// Incoterm.Id (Guid) — do NOT use v1/Incoterms/Lookup, whose id is a name string.
export const lookupIncoterm = () =>
  crudeApi
    .get(`${PROCUREMENT}/LookupIncoterm`)
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// GET /v1/CargoCategories/Lookup — ⚠️ ProHydro host (proHydroApi), per the
// contract's "host WebApi terpisah" note + curl (401 prohydro / 404 crude).
export const lookupCargoCategories = () =>
  proHydroApi
    .get('/v1/CargoCategories/Lookup')
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// GET /v1/CDU/GetListCDU (crude). Feeds the cargo Tujuan/Unit picker; each item
// carries the Plant so the UI can group by it. Returns raw items.
export const getCduList = ({ page = 1, pageSize = 100 } = {}) =>
  crudeApi
    .get('/v1/CDU/GetListCDU', { page, pageSize })
    .then(res => (Array.isArray(res?.items) ? res.items : []));
